import React, { FC, MutableRefObject } from 'react';
import { makeStyles } from 'tss-react/mui';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import AlarmOn from '@mui/icons-material/AlarmOn';

import { transactionsTypes } from 'companion-app-components/flux/transactions';
import type RootState from 'companion-app-components/utils/redux-store/rootState';
import { scheduledTransactionsSelectors } from 'companion-app-components/flux/scheduled-transactions';

import QMenu from 'components/QMenu';
import QCheckbox from 'components/QCheckbox';
import QTip from 'components/QuickenControls/QTip';

import { isC2REnabledAccount } from 'data/accounts/utils';
import { getTransactionsByAccountId, isTransferWritableAccountAvailable } from 'data/transactions/selectors';
import { getTxnStateInfo, isNextScheduledInstance, transactionEditsNotAllowed,
  isUnacceptedScheduledTxn, nextScheduledInstanceAfterDate, isBankOwnedPending } from 'data/transactions/utils';
import { hasDatasetWriteAccess } from 'data/datasetSharing/selectors';


import { RegListItem, styles } from '../styles';
import { QUEUE_PAYMENTS } from '../utils';
import type { AttributesType, CallbacksType } from '../types';

const useStyles = makeStyles()(styles as Record<string, any>);

interface SelectFieldProps {
  attributes: AttributesType;
  forceStatic: boolean;
  callbacks: CallbacksType;
  editable: boolean;
  SHOW_ALL: boolean;
  fieldActive: boolean;
  txn: transactionsTypes.CashFlowTransaction;
  currEditFieldName: string;
  isAccountNotSynced: boolean;
  isTypeGrossPaycheck: boolean;
  selected: boolean;
  onRowSelect: (txn: transactionsTypes.CashFlowTransaction, isSelected: boolean) => void;
  msgGrossPaycheck: string;
  focusField: MutableRefObject<HTMLElement | null>;
  isTxnDirty: boolean;
  showNewTxn: boolean;
}

const SelectField: FC<SelectFieldProps> = ({
  attributes,
  forceStatic,
  callbacks,
  editable,
  SHOW_ALL,
  fieldActive,
  txn,
  currEditFieldName,
  isAccountNotSynced,
  isTypeGrossPaycheck, 
  selected,
  onRowSelect,
  msgGrossPaycheck,
  focusField,
  isTxnDirty = false,
  showNewTxn = false,
}) => {

  const { classes, theme } = useStyles();

  const transactionsByAccountId = useSelector((state: RootState) => getTransactionsByAccountId(state, undefined));
  const scheduledTransactionsById = useSelector((state: RootState) => scheduledTransactionsSelectors.getScheduledTransactions(state));
  const hasWriteAccess = useSelector((state: RootState) => hasDatasetWriteAccess(state));
  const isTransferAccountPresent = useSelector((state: RootState) => isTransferWritableAccountAvailable(state, txn));

  const hasEditAccess = hasWriteAccess && isTransferAccountPresent;
  const isC2REnabled = isC2REnabledAccount(txn.accountId);
  const isQueuePmt = (!isTxnDirty || showNewTxn) && QUEUE_PAYMENTS.includes(txn.check?.number ?? '');

  const renderRecurringField = () => {

    const accountTransactions = transactionsByAccountId.get(txn.accountId ?? '');
    let qtipTitle: string | null = null;
    const recurringStatus = getTxnStateInfo(txn);

    const nextInstance = accountTransactions &&
      nextScheduledInstanceAfterDate(DateTime.now().toISODate()!, accountTransactions as any, txn.stModelId ?? '');
    const isNext = nextInstance && nextInstance.id === txn.id;
    const isUpcoming = isNextScheduledInstance(txn, scheduledTransactionsById);

    let menuOptions: { label: string, value: string }[] | null = isUpcoming ?
      [
        { label: 'Enter this reminder', value: 'ACCEPT' },
        { label: 'Ignore this reminder', value: 'SKIP' },
      ]
      :
      [
        { label: 'Enter this and all previous overdue reminders for this Payee as Paid', value: 'ACCEPT' },
        { label: 'Ignore this and all previous overdue reminders for this Payee', value: 'SKIP' },
      ];

    let iconClass = '';

    switch (recurringStatus.status) {

      case 'DUE':
        qtipTitle = 'This reminder is due';
        iconClass = 'due';
        break;

      case 'OVERDUE':
        qtipTitle = 'This reminder is overdue';
        iconClass = 'overdue';
        break;

      default:
        if (isNext) {
          qtipTitle = 'This will be the next instance that becomes due';
        } else {
          qtipTitle = 'This reminder is not yet due';
          iconClass = 'notdue';
          menuOptions = null;
        }
        break;
    }

    // if this scheduled instance contains transfers to unsynced accounts, then do not allow the user to act
    // @ts-expect-error: An argument for 'state' was not provided.
    if (transactionEditsNotAllowed(txn)) {
      qtipTitle = 'This scheduled instance is a billpay transaction, or contains a transfer to an unsynced account ' +
        'or account that does not support transactions.' +
        ' You can only edit or act upon it from the desktop application';
      menuOptions = null;
    } else if (isTypeGrossPaycheck) {
      qtipTitle = msgGrossPaycheck;
      menuOptions = null;
    }
    const customTrigger = (
      <AlarmOn className={classes[iconClass]} />
    );

    if (menuOptions) {
      return (
        <QMenu
          customTrigger={customTrigger}
          name={`${txn.id}-recurring`}
          title={qtipTitle}
          // @ts-expect-error: Type '"mediumNoPadding"' is not assignable
          menuIconButtonSize="mediumNoPadding"
          autoFocus={fieldActive}
          options={menuOptions}
          onChange={(val) => callbacks.actOnScheduledTxn?.(txn, val)}
          // @ts-expect-error: null is not assignable to type
          buttonRef={fieldActive ? (input) => { 
            focusField.current = input; // eslint-disable-line
          } : null}
        />
      );
    }
    return (
      <QTip
        title={qtipTitle}
        wrapId={`${txn.id}-recurring`}
      >
        {customTrigger}
      </QTip>
    );
  };

  return (
    <RegListItem
      {...attributes}
      tabIndex={null}
      onClick={null}
      className={classes.iconButtonColumn}
    >
      {
        isUnacceptedScheduledTxn(txn) && renderRecurringField()
      }
      {
        !isUnacceptedScheduledTxn(txn) && SHOW_ALL &&
        <QCheckbox
          // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
          size={theme.components?.register.checkboxSize}
          color="primary"
          inputProps={{ 'aria-label': 'Select or Unselect' }}
          autoFocus={editable && currEditFieldName === 'select'}
          disabled={!hasEditAccess || (isAccountNotSynced && !editable) || forceStatic || (isBankOwnedPending(txn) && isC2REnabled) || isTypeGrossPaycheck || isQueuePmt}
          checked={selected}
          inputRef={editable && !forceStatic && currEditFieldName === 'select' ?
            (input) => {
              focusField.current = input; // eslint-disable-line
            } : null}
          onClick={() => onRowSelect(txn, !selected)}
        />
      }
    </RegListItem>
  );
};

export default SelectField;
